import {
  Button,
  Checkbox,
  Collapse,
  Empty,
  Input,
  InputNumber,
  List,
  Tag,
  Typography,
  message,
} from "antd";
import { CheckCircle, X, Plus, Minus, ChevronDown, ChevronRight } from "lucide-react";
import React, { useState, useEffect } from "react";
import { SuppliesType } from "~/utils/constants";
import { useGetServiceTicketSupplies } from "~/services/supply";
import { TServiceTicket, TServiceTicketSupplyItem, TSupply } from "~/types";
import {
  SERVICE_TICKET_KEYS,
  useUpdateServiceTicket,
} from "~/services/service-ticket";
import { useQueryClient } from "@tanstack/react-query";
import { useNotification } from "~/hooks/useNotification";
import { AddSuppliesModal } from "./AddSuppliesModal";

const { Text } = Typography;
const { Search: AntSearch } = Input;

interface SuppliesCheckListProps {
  ticket: TServiceTicket;
  onSuppliesChecked?: (supplyIds: string[], checked: boolean, confirmedSupplies?: Record<string, { quantity: number; confirmed: boolean }>) => void;
}

interface GroupedSupplies {
  [category: string]: Array<TServiceTicketSupplyItem & { supply: TSupply }>;
}

export const SuppliesCheckList: React.FC<SuppliesCheckListProps> = ({
  ticket,
  onSuppliesChecked,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [checkedSupplies, setCheckedSupplies] = useState<
    Record<string, boolean>
  >({});
  const [supplyQuantities, setSupplyQuantities] = useState<
    Record<string, number>
  >({});
  const [showAddSuppliesModal, setShowAddSuppliesModal] = useState(false);

  const { data: supplies, refetch } = useGetServiceTicketSupplies(ticket.id);

  const updateMutation = useUpdateServiceTicket();
  const qc = useQueryClient();

  const { success, error } = useNotification();

  // Initialize checked state and quantities for existing supplies
  useEffect(() => {
    const initialQuantities: Record<string, number> = {};
    const initialChecked: Record<string, boolean> = {};
    supplies?.forEach((item) => {
      if (supplyQuantities[item.supplyId] === undefined) {
        initialQuantities[item.supplyId] = item.quantity || 1;
      }
      if (checkedSupplies[item.supplyId] === undefined) {
        initialChecked[item.supplyId] = false;
      }
    });
    if (
      Object.keys(initialQuantities).length > 0 ||
      Object.keys(initialChecked).length > 0
    ) {
      setSupplyQuantities((prev) => ({ ...prev, ...initialQuantities }));
      setCheckedSupplies((prev) => ({ ...prev, ...initialChecked }));
    }
  }, [checkedSupplies, supplies, supplyQuantities]);

  const filteredSupplies = supplies?.filter((item) => {
    const supply = item.supply;
    if (!supply) return false;
    const searchLower = searchTerm.toLowerCase();
    return (
      supply.name?.toLowerCase().includes(searchLower) ||
      supply.supplyRef?.toLowerCase().includes(searchLower) ||
      supply.brand?.toLowerCase().includes(searchLower) ||
      supply.category?.toLowerCase().includes(searchLower) ||
      false
    );
  });

  // Group supplies by category
  const groupedSupplies: GroupedSupplies = {};
  filteredSupplies?.forEach((item) => {
    const category = item.supply?.category || 'Other';
    if (!groupedSupplies[category]) {
      groupedSupplies[category] = [];
    }
    groupedSupplies[category].push(item as TServiceTicketSupplyItem & { supply: TSupply });
  });

  // State for collapsed groups (mobile)
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Listen for window resize to detect mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleGroup = (category: string) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const allChecked =
    filteredSupplies?.length &&
    filteredSupplies?.every((item) => checkedSupplies[item.supplyId]);

  const formatSuppliesType = (suppliesType?: SuppliesType) => {
    if (!suppliesType) return "N/A";
    const typeMap: Record<SuppliesType, string> = {
      [SuppliesType.PACKAGING_MATERIALS]: "Packaging Materials",
      [SuppliesType.TOOLS_KITS_AND_HARDWARE]: "Tools, Kits & Hardware",
      [SuppliesType.PPE]: "PPE",
      [SuppliesType.EQUIPMENT]: "Equipment",
      [SuppliesType.QUICK_PACK]: "Quick Pack",
      [SuppliesType.INTERNAL_SUPPLIES_AND_FACILITIES]:
        "Internal Supplies & Facilities",
    };
    return typeMap[suppliesType] || suppliesType;
  };

  const handleCheckSupply = (supplyId: string, checked: boolean) => {
    const newCheckedSupplies = {
      ...checkedSupplies,
      [supplyId]: checked,
    };
    setCheckedSupplies(newCheckedSupplies);

    // Build confirmed supplies record with quantities
    const confirmedSupplies: Record<string, { quantity: number; confirmed: boolean }> = {};
    Object.keys(newCheckedSupplies).forEach((id) => {
      if (newCheckedSupplies[id]) {
        confirmedSupplies[id] = {
          quantity: supplyQuantities[id] || 1,
          confirmed: true,
        };
      }
    });

    message.success(`Supply ${checked ? "checked" : "unchecked"} successfully`);

    // Notify parent component
    if (onSuppliesChecked) {
      onSuppliesChecked([supplyId], checked, confirmedSupplies);
    }
  };

  const handleQuantityChange = (supplyId: string, quantity: number | null) => {
    if (quantity === null || quantity < 0) return;
    const supply = supplies?.find((item) => item.supplyId === supplyId)?.supply;
    const maxQuantity = supply?.currentStock ?? 999;
    if (quantity > maxQuantity) return;

    const newQuantities = {
      ...supplyQuantities,
      [supplyId]: quantity,
    };
    setSupplyQuantities(newQuantities);

    // Build confirmed supplies record with updated quantities
    const confirmedSupplies: Record<string, { quantity: number; confirmed: boolean }> = {};
    Object.keys(checkedSupplies).forEach((id) => {
      if (checkedSupplies[id]) {
        confirmedSupplies[id] = {
          quantity: newQuantities[id] || 1,
          confirmed: true,
        };
      }
    });

    // Notify parent component of quantity change
    if (onSuppliesChecked && checkedSupplies[supplyId]) {
      onSuppliesChecked([supplyId], true, confirmedSupplies);
    }
  };

  const handleSaveSupplies = async (
    selectedSupplies: Record<string, { supply: TSupply; quantity: number }>
  ) => {
    try {
      const newSupplyItems: Omit<TServiceTicketSupplyItem, "id">[] =
        Object.values(selectedSupplies).map(({ supply, quantity }) => ({
          supplyId: supply.id,
          quantity,
          unitCost: Number(supply.cost) || 0,
          supply,
        }));

      // Combine existing supplies with new ones
      const updatedSupplies = [...(supplies ?? []), ...newSupplyItems];

      const supplyItems = updatedSupplies.map((item) => ({
        supplyId: item.supplyId,
        quantity: item.quantity,
        unitCost: Number(item.supply?.cost ?? 0),
      }));

      await updateMutation.mutateAsync({
        id: ticket.id,
        supplyItems,
      });

      qc.invalidateQueries({
        queryKey: SERVICE_TICKET_KEYS.GET_MY_SERVICE_TICKETS({}),
      });
      success({ message: "Supplies updated successfully" });
      refetch();
      setShowAddSuppliesModal(false);
    } catch (err) {
      console.error("Error updating supplies:", err);
      error({ message: "Failed to update supplies" });
    }
  };

  const handleCheckAll = (checked: boolean) => {
    const newCheckedSupplies = { ...checkedSupplies };
    const supplyIds: string[] = [];

    filteredSupplies?.forEach((item) => {
      newCheckedSupplies[item.supplyId] = checked;
      supplyIds.push(item.supplyId);
    });

    setCheckedSupplies(newCheckedSupplies);

    // Build confirmed supplies record
    const confirmedSupplies: Record<string, { quantity: number; confirmed: boolean }> = {};
    Object.keys(newCheckedSupplies).forEach((id) => {
      if (newCheckedSupplies[id]) {
        confirmedSupplies[id] = {
          quantity: supplyQuantities[id] || 1,
          confirmed: true,
        };
      }
    });

    if (supplyIds.length > 0) {
      message.success(
        `${supplyIds.length} items ${
          checked ? "checked" : "unchecked"
        } successfully`
      );

      // Notify parent component
      if (onSuppliesChecked) {
        onSuppliesChecked(supplyIds, checked, confirmedSupplies);
      }
    }
  };

  const getStockStatusColor = (supply?: TSupply) => {
    if (!supply) return "gray";
    const currentStock = supply.currentStock ?? 0;
    const minimumStock = supply.minimumStock ?? 0;

    if (currentStock <= 0) return "red";
    if (currentStock <= minimumStock) return "orange";
    return "green";
  };

  const getStockStatusText = (supply?: TSupply) => {
    if (!supply) return "N/A";
    const currentStock = supply.currentStock ?? 0;
    const minimumStock = supply.minimumStock ?? 0;

    if (currentStock <= 0) return "Out of Stock";
    if (currentStock <= minimumStock) return "Low Stock";
    return "In Stock";
  };

  return (
    <div className="supplies-check-list">
      <div className="mb-4 space-y-2">
        <div className="w-full">
          <AntSearch
            placeholder={isMobile ? "Search supplies..." : "Search by name, reference, brand, or category..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            size={isMobile ? "middle" : "large"}
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="default"
            onClick={() => setShowAddSuppliesModal(true)}
            className="flex items-center"
            size={isMobile ? "small" : "middle"}
          >
            <Plus size={14} className={isMobile ? "" : "mr-1"} />
            {!isMobile && "Add Supplies"}
          </Button>
          {filteredSupplies?.length && (
            <Button
              type="primary"
              onClick={() => handleCheckAll(!allChecked)}
              className="flex items-center"
              size={isMobile ? "small" : "middle"}
            >
              <div className="flex items-center">
                {allChecked ? (
                  <>
                    <X size={14} className={isMobile ? "" : "mr-1"} />
                    {!isMobile && "Uncheck All"}
                  </>
                ) : (
                  <>
                    <CheckCircle size={14} className={isMobile ? "" : "mr-1"} />
                    {!isMobile && "Check All"}
                  </>
                )}
              </div>
            </Button>
          )}
        </div>
      </div>

      {!filteredSupplies?.length ? (
        <Empty description="No supplies found" />
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedSupplies).map(([category, categorySupplies]) => {
            const isCollapsed = collapsedGroups[category];
            const categoryCheckedCount = categorySupplies.filter(item => checkedSupplies[item.supplyId]).length;

            return (
              <div key={category} className="border rounded-lg">
                {/* Category Header */}
                <div
                  className={`flex items-center justify-between p-3 bg-gray-50 rounded-t-lg border-b cursor-pointer hover:bg-gray-100 transition-colors ${
                    isMobile ? 'sticky top-0 z-10' : ''
                  }`}
                  onClick={() => isMobile && toggleGroup(category)}
                >
                  <div className="flex items-center space-x-2">
                    {isMobile && (
                      <div className="text-gray-500">
                        {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                      </div>
                    )}
                    <Text strong className="text-sm">
                      {category}
                    </Text>
                    <Tag color="blue" className="text-xs">
                      {categorySupplies.length} items
                    </Tag>
                    {categoryCheckedCount > 0 && (
                      <Tag color="green" className="text-xs">
                        {categoryCheckedCount} checked
                      </Tag>
                    )}
                  </div>

                  {!isMobile && (
                    <Button
                      type="text"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        const allCategoryChecked = categorySupplies.every(item => checkedSupplies[item.supplyId]);
                        categorySupplies.forEach(item => {
                          handleCheckSupply(item.supplyId, !allCategoryChecked);
                        });
                      }}
                      className="text-xs"
                    >
                      {categorySupplies.every(item => checkedSupplies[item.supplyId]) ? 'Uncheck All' : 'Check All'}
                    </Button>
                  )}
                </div>

                {/* Category Content */}
                <div className={`${isMobile && isCollapsed ? 'hidden' : 'block'}`}>
                  {isMobile ? (
                    /* Mobile: Compact card layout */
                    <div className="divide-y">
                      {categorySupplies.map((item) => {
                        const supply = item.supply;
                        if (!supply) return null;

                        return (
                          <div key={item.supplyId} className="p-3 hover:bg-gray-50">
                            <div className="flex items-start space-x-3">
                              <Checkbox
                                checked={checkedSupplies[item.supplyId]}
                                onChange={(e) => handleCheckSupply(item.supplyId, e.target.checked)}
                                className="mt-1"
                              />

                              <div className="flex-1 min-w-0">
                                <div className="mb-1">
                                  <Text strong className="text-sm">
                                    {supply.name || "Untitled Supply"}
                                  </Text>
                                </div>

                                <div className="space-y-1 text-xs text-gray-500">
                                  {supply.brand && <div>Brand: {supply.brand}</div>}
                                  {supply.category && <div>Category: {supply.category}</div>}
                                </div>

                                {checkedSupplies[item.supplyId] && (
                                  <div className="flex items-center space-x-2 mt-2 pt-2 border-t">
                                    <Text className="text-xs text-gray-600 flex-shrink-0">Qty:</Text>
                                    <div className="flex items-center space-x-1">
                                      <Button
                                        type="text"
                                        size="small"
                                        icon={<Minus size={10} />}
                                        onClick={() =>
                                          handleQuantityChange(
                                            item.supplyId,
                                            Math.max(0, (supplyQuantities[item.supplyId] || item.quantity || 1) - 1)
                                          )
                                        }
                                        disabled={(supplyQuantities[item.supplyId] || item.quantity || 1) <= 0}
                                        className="h-5 w-5 p-0 flex items-center justify-center"
                                      />
                                      <InputNumber
                                        min={0}
                                        max={supply.currentStock ?? 999}
                                        value={supplyQuantities[item.supplyId] || item.quantity || 1}
                                        onChange={(value) => handleQuantityChange(item.supplyId, value)}
                                        size="small"
                                        className="w-12 text-center text-xs"
                                        controls={false}
                                      />
                                      <Button
                                        type="text"
                                        size="small"
                                        icon={<Plus size={10} />}
                                        onClick={() =>
                                          handleQuantityChange(
                                            item.supplyId,
                                            Math.min(
                                              supply.currentStock ?? 999,
                                              (supplyQuantities[item.supplyId] || item.quantity || 1) + 1
                                            )
                                          )
                                        }
                                        disabled={
                                          (supplyQuantities[item.supplyId] || item.quantity || 1) >= (supply.currentStock ?? 999)
                                        }
                                        className="h-5 w-5 p-0 flex items-center justify-center"
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    /* Desktop: Original list layout */
                    <List
                      itemLayout="horizontal"
                      dataSource={categorySupplies}
                      renderItem={(item) => {
                        const supply = item.supply;
                        if (!supply) return null;

                        return (
                          <List.Item className="py-4 px-4 hover:bg-gray-50" key={item.supplyId}>
                            <div className="flex items-start w-full gap-6">
                              <div className="mt-1">
                                <Checkbox
                                  checked={checkedSupplies[item.supplyId]}
                                  onChange={(e) => handleCheckSupply(item.supplyId, e.target.checked)}
                                />
                              </div>

                              <div className="flex-1 min-w-0">
                                <div>
                                  <div className="font-medium mb-2">
                                    {supply.name || "Untitled Supply"}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {supply.brand && <span>Brand: {supply.brand}</span>}
                                    {supply.brand && supply.category && <span className="mx-2">·</span>}
                                    {supply.category && <span>Category: {supply.category}</span>}
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center gap-4 min-w-[280px]">
                                <div className="flex items-center gap-2">
                                  <Text type="secondary" className="text-xs whitespace-nowrap">
                                    Quantity Used:
                                  </Text>
                                  <InputNumber
                                    min={0}
                                    max={supply.currentStock ?? 999}
                                    value={supplyQuantities[item.supplyId] || item.quantity || 1}
                                    onChange={(value) => handleQuantityChange(item.supplyId, value)}
                                    disabled={!checkedSupplies[item.supplyId]}
                                    size="small"
                                    style={{ width: 100 }}
                                    addonAfter={supply.unit || "units"}
                                  />
                                </div>
                                {checkedSupplies[item.supplyId] && (
                                  <div className="flex items-center gap-2">
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<Minus size={14} />}
                                      onClick={() =>
                                        handleQuantityChange(
                                          item.supplyId,
                                          Math.max(0, (supplyQuantities[item.supplyId] || item.quantity || 1) - 1)
                                        )
                                      }
                                      disabled={(supplyQuantities[item.supplyId] || item.quantity || 1) <= 0}
                                      className="hover:bg-gray-100"
                                    />
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<Plus size={14} />}
                                      onClick={() =>
                                        handleQuantityChange(
                                          item.supplyId,
                                          Math.min(
                                            supply.currentStock ?? 999,
                                            (supplyQuantities[item.supplyId] || item.quantity || 1) + 1
                                          )
                                        )
                                      }
                                      disabled={
                                        (supplyQuantities[item.supplyId] || item.quantity || 1) >= (supply.currentStock ?? 999)
                                      }
                                      className="hover:bg-gray-100"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </List.Item>
                        );
                      }}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AddSuppliesModal
        open={showAddSuppliesModal}
        onCancel={() => setShowAddSuppliesModal(false)}
        onSave={handleSaveSupplies}
        existingSupplies={supplies}
        loading={updateMutation.isPending}
      />
    </div>
  );
};
